import React, { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { FileUpload as UploadIcon } from '@mui/icons-material';
import stockCountImportService, {
  extractStockCountImportError,
  type StockCountImportResult,
  type StockCountImportRowStatus,
} from '../../services/stockCountImportService';

interface Props {
  open: boolean;
  venueId: string;
  countId: string;
  onClose: () => void;
  /** Called after a successful apply so the parent can refetch the count. */
  onApplied: () => void;
}

type Step = 'idle' | 'previewing' | 'previewed' | 'committing' | 'committed';

const STATUS_COLOR: Record<StockCountImportRowStatus, string> = {
  MATCHED: '#1a7f37',
  IGNORED_BLANK: '#57606a',
  INVALID_QTY: '#cf222e',
  DUPLICATE: '#cf222e',
  OUT_OF_SCOPE: '#9a6700',
};

const SUMMARY_FIELDS: { key: keyof StockCountImportResult['summary']; label: string }[] = [
  { key: 'totalRows', label: 'Total' },
  { key: 'matchedRows', label: 'Matched' },
  { key: 'ignoredRows', label: 'Ignored (blank)' },
  { key: 'invalidRows', label: 'Invalid' },
  { key: 'duplicateRows', label: 'Duplicate' },
  { key: 'outOfScopeRows', label: 'Out of scope' },
];

const StockCountImportDialog: React.FC<Props> = ({ open, venueId, countId, onClose, onApplied }) => {
  const [step, setStep] = useState<Step>('idle');
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<StockCountImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setStep('idle');
    setFile(null);
    setResult(null);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    setResult(null);
    setError(null);
    setStep('idle');
  };

  const handlePreview = async () => {
    if (!file) return;
    setStep('previewing');
    setError(null);
    try {
      const res = await stockCountImportService.preview(venueId, countId, file);
      setResult(res);
      setStep('previewed');
    } catch (err) {
      setError(extractStockCountImportError(err));
      setStep('idle');
    }
  };

  const handleApply = async () => {
    if (!file) return;
    setStep('committing');
    setError(null);
    try {
      const res = await stockCountImportService.commit(venueId, countId, file);
      setResult(res);
      setStep('committed');
      onApplied(); // refetch the count in the parent
    } catch (err) {
      setError(extractStockCountImportError(err));
      setStep('previewed');
    }
  };

  const matchedRows = result?.summary.matchedRows ?? 0;
  const busy = step === 'previewing' || step === 'committing';

  return (
    <Dialog open={open} onClose={busy ? undefined : handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{step === 'committed' ? 'Import Complete' : 'Import Count Sheet'}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Upload the filled-in count sheet. Preview the changes first, then apply.
            Only the <strong>Physical Count</strong> column updates existing count lines —
            applying does not post the count.
          </Typography>

          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

          {step !== 'committed' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadIcon />}
                disabled={busy}
                sx={{ textTransform: 'none' }}
              >
                {file ? 'Change file' : 'Choose file'}
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                  onChange={handleFileChange}
                />
              </Button>
              {file && <Typography variant="body2" noWrap>{file.name}</Typography>}
            </Box>
          )}

          {result && (
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {SUMMARY_FIELDS.map(f => (
                <Box key={f.key as string} sx={{ minWidth: 90 }}>
                  <Typography variant="h6" fontWeight={700}>{result.summary[f.key] ?? 0}</Typography>
                  <Typography variant="caption" color="text.secondary">{f.label}</Typography>
                </Box>
              ))}
              {step === 'committed' && (
                <Box sx={{ minWidth: 90 }}>
                  <Typography variant="h6" fontWeight={700} color="success.main">{result.summary.appliedRows ?? 0}</Typography>
                  <Typography variant="caption" color="text.secondary">Applied</Typography>
                </Box>
              )}
            </Box>
          )}

          {result && result.rows.length > 0 && (
            <TableContainer sx={{ maxHeight: 340 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {['Row', 'Status', 'Item', 'System', 'Physical', 'Variance', 'Note'].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.rows.map(row => (
                    <TableRow key={row.rowNumber} hover>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>{row.rowNumber}</TableCell>
                      <TableCell>
                        <Chip
                          label={row.status.replace(/_/g, ' ')}
                          size="small"
                          sx={{ fontSize: '0.62rem', height: 18, fontWeight: 700, color: STATUS_COLOR[row.status], backgroundColor: `${STATUS_COLOR[row.status]}1a`, border: 'none' }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', maxWidth: 200 }}>
                        <Typography variant="body2" noWrap>{row.itemName ?? row.inventoryItemId}</Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem' }}>{row.expectedQty ?? '—'}</TableCell>
                      <TableCell sx={{ fontSize: '0.78rem' }}>{row.physicalCount ?? '—'}</TableCell>
                      <TableCell sx={{ fontSize: '0.78rem' }}>{row.varianceQty ?? '—'}</TableCell>
                      <TableCell sx={{ fontSize: '0.7rem', color: 'text.disabled', maxWidth: 200 }}>
                        <Typography variant="caption" noWrap>{row.message ?? ''}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {step === 'previewed' && matchedRows === 0 && (
            <Alert severity="warning">No matched rows to apply. Check that Physical Count is filled and items are on this count.</Alert>
          )}
          {step === 'committed' && (
            <Alert severity="success">Applied {result?.summary.appliedRows ?? 0} count(s) to the draft. Review and post separately when ready.</Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={busy} sx={{ textTransform: 'none' }}>
          {step === 'committed' ? 'Close' : 'Cancel'}
        </Button>
        {step !== 'committed' && (
          <>
            <Button
              onClick={handlePreview}
              disabled={!file || busy}
              variant={step === 'previewed' ? 'outlined' : 'contained'}
              startIcon={step === 'previewing' ? <CircularProgress size={16} /> : undefined}
              sx={{ textTransform: 'none' }}
            >
              {step === 'previewing' ? 'Previewing…' : 'Preview'}
            </Button>
            <Button
              onClick={handleApply}
              disabled={step !== 'previewed' || matchedRows === 0 || busy}
              variant="contained"
              startIcon={step === 'committing' ? <CircularProgress size={16} /> : undefined}
              sx={{ textTransform: 'none' }}
            >
              {step === 'committing' ? 'Applying…' : `Apply ${matchedRows > 0 ? `(${matchedRows})` : ''}`}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default StockCountImportDialog;
